import { Schema } from "effect";

import {
  DashboardArtifactCell,
  DashboardDateRangePreset,
  DefinitionText,
  findDuplicate,
  Identifier,
  IsoDate,
  IsoTimestamp,
  Label,
  NonNegativeCount,
  PositiveCount,
  ShortText,
} from "./dashboardSchemaPrimitives";

/**
 * Metrics over clean tables. A `MetricDefinition` is the browser-facing view
 * of one registry entry; a `MetricQuery` asks for metric values over a date
 * range, split by dimensions; a `MetricValue` is one answer row. `Provenance`
 * says where a number came from and `ChangeExplanation` says why a closed
 * period's number moved since it was first reported.
 */

export const METRIC_LIMITS = {
  metricsPerQuery: 8,
  dimensionsPerQuery: 3,
  filtersPerQuery: 10,
  filterValues: 200,
  dimensionsPerMetric: 30,
  synonyms: 20,
  definitionChanges: 50,
} as const;

/** Registry ids are dotted snake case, e.g. `care_navigation.billed_completion`. */
export const MetricId = Schema.String.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, {
    message: "Use dotted snake case, e.g. care_navigation.billed_completion",
  }),
);
export type MetricId = typeof MetricId.Type;

// Definitions --------------------------------------------------------------

export const MetricFormat = Schema.Literals(["count", "percent", "currency"]);
export type MetricFormat = typeof MetricFormat.Type;

/** A measure that can be summed across periods and splits. */
export const MetricSummableMeasure = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("count"), label: Label }),
  Schema.Struct({ kind: Schema.Literal("sum"), column: Identifier, label: Label }),
]);
export type MetricSummableMeasure = typeof MetricSummableMeasure.Type;

/**
 * How the fact table rolls up. A ratio divides two summable measures after
 * summing each, so a KPI comparison is computed from summed counts.
 */
export const MetricAggregate = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("count") }),
  Schema.Struct({ kind: Schema.Literal("count_distinct"), column: Identifier }),
  Schema.Struct({ kind: Schema.Literal("sum"), column: Identifier }),
  Schema.Struct({
    kind: Schema.Literal("ratio"),
    numerator: MetricSummableMeasure,
    denominator: MetricSummableMeasure,
  }),
  Schema.Struct({
    kind: Schema.Literal("percentile"),
    column: Identifier,
    p: Schema.Finite.check(Schema.isGreaterThan(0), Schema.isLessThan(100)),
  }),
]);
export type MetricAggregate = typeof MetricAggregate.Type;

export const MetricDimensionType = Schema.Literals(["text", "number", "boolean", "date"]);
export type MetricDimensionType = typeof MetricDimensionType.Type;

/**
 * A split. `current` dimensions use today's value (a member's current
 * navigator); `as_of` dimensions use the value when the fact happened.
 * `type` types filter values. `phi` dimensions are never shown to viewers
 * without PHI access.
 */
export const MetricDimension = Schema.Struct({
  id: Identifier,
  label: Label,
  type: MetricDimensionType,
  phi: Schema.Boolean,
  temporal: Schema.Literals(["current", "as_of"]),
});
export type MetricDimension = typeof MetricDimension.Type;

/** The finest period a metric is stored at; `snapshot` is a point-in-time count. */
export const MetricGrain = Schema.Literals(["day", "week", "month", "snapshot"]);
export type MetricGrain = typeof MetricGrain.Type;

/** The period a query reports by; `total` is one value for the whole range. */
export const MetricQueryGrain = Schema.Literals([
  "day",
  "week",
  "month",
  "quarter",
  "year",
  "total",
]);
export type MetricQueryGrain = typeof MetricQueryGrain.Type;

/**
 * Ranks for "a query grain at or above the native grain". A snapshot metric
 * can be read at any grain (the last snapshot in each period).
 */
export const METRIC_GRAIN_RANK: Readonly<Record<MetricGrain | MetricQueryGrain, number>> = {
  snapshot: 0,
  day: 1,
  week: 2,
  month: 3,
  quarter: 4,
  year: 5,
  total: 6,
};

export const MetricVerificationStatus = Schema.Literals(["verified", "unverified", "failing"]);
export type MetricVerificationStatus = typeof MetricVerificationStatus.Type;

/** The latest check of the metric against its reference numbers. */
export const MetricVerification = Schema.Struct({
  status: MetricVerificationStatus,
  checkedAt: Schema.NullOr(IsoTimestamp),
  reason: Schema.optionalKey(ShortText),
});
export type MetricVerification = typeof MetricVerification.Type;

/** Recent periods keep changing for `days` after they close, for `reason`. */
export const MetricSettling = Schema.Struct({
  days: NonNegativeCount.check(Schema.isLessThanOrEqualTo(366)),
  reason: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
});
export type MetricSettling = typeof MetricSettling.Type;

/** Output column names of a metric query; dimension ids may not reuse them. */
export const METRIC_OUTPUT_COLUMNS = [
  "metric",
  "period",
  "value",
  "numerator",
  "denominator",
  "compare_value",
] as const;
export type MetricOutputColumn = (typeof METRIC_OUTPUT_COLUMNS)[number];

const MetricDefinitionFields = Schema.Struct({
  id: MetricId,
  title: Label,
  description: DefinitionText,
  synonyms: Schema.Array(Label).check(Schema.isMaxLength(METRIC_LIMITS.synonyms)),
  format: MetricFormat,
  higherIsBetter: Schema.Boolean,
  aggregate: MetricAggregate,
  /** The clean table the metric reads, e.g. `clean.care_navigation_sessions`. */
  fact: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  dimensions: Schema.Array(MetricDimension).check(
    Schema.isMaxLength(METRIC_LIMITS.dimensionsPerMetric),
  ),
  grain: MetricGrain,
  settling: Schema.optionalKey(MetricSettling),
  /** `custom` metrics take no splits and no change explanation, and are labelled in the UI. */
  kind: Schema.Literals(["standard", "custom"]),
  verified: MetricVerification,
  /** Bumped whenever the definition changes; provenance and explanations cite it. */
  definitionVersion: PositiveCount,
});

export const MetricDefinition = MetricDefinitionFields.check(
  Schema.makeFilter(
    (metric) => {
      const duplicate = findDuplicate(metric.dimensions.map((dimension) => dimension.id));
      if (duplicate) return { path: ["dimensions"], message: `Duplicate dimension "${duplicate}"` };
      const reserved = metric.dimensions.findIndex((dimension) =>
        (METRIC_OUTPUT_COLUMNS as ReadonlyArray<string>).includes(dimension.id),
      );
      if (reserved !== -1) {
        return {
          path: ["dimensions", reserved, "id"],
          message: `"${metric.dimensions[reserved]!.id}" is a metric output column name`,
        };
      }
      if (metric.kind === "custom" && metric.dimensions.length > 0) {
        return { path: ["dimensions"], message: "Custom metrics have no dimensions" };
      }
      return undefined;
    },
    { identifier: "MetricDefinition" },
  ),
);
export type MetricDefinition = typeof MetricDefinition.Type;

// Queries ------------------------------------------------------------------

/**
 * Inside a dashboard spec, `parameter` binds a `date_range` parameter.
 * Outside one there are no parameters, so a resolver rejects it.
 */
export const MetricDateRange = Schema.Union([
  Schema.Struct({ preset: DashboardDateRangePreset }),
  Schema.Struct({ start: IsoDate, end: IsoDate }),
  Schema.Struct({ parameter: Identifier }),
]);
export type MetricDateRange = typeof MetricDateRange.Type;

export const MetricFilterValue = Schema.Union([Schema.String, Schema.Finite, Schema.Boolean]);
export type MetricFilterValue = typeof MetricFilterValue.Type;

/**
 * Keeps (`in`) or drops (`not_in`) rows by a dimension. Values are typed by
 * the dimension. Inside a dashboard spec, `parameter` binds a `select` or
 * `multi_select` parameter instead of fixed values.
 */
export const MetricFilter = Schema.Union([
  Schema.Struct({
    dimension: Identifier,
    op: Schema.Literals(["in", "not_in"]),
    values: Schema.Array(MetricFilterValue).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(METRIC_LIMITS.filterValues),
    ),
  }),
  Schema.Struct({
    dimension: Identifier,
    op: Schema.Literals(["in", "not_in"]),
    parameter: Identifier,
  }),
]);
export type MetricFilter = typeof MetricFilter.Type;

export const MetricComparison = Schema.Literals(["previous_period", "same_period_last_year"]);
export type MetricComparison = typeof MetricComparison.Type;

/**
 * `now` reads today's numbers; `reported` reads each closed period as it was
 * first reported at close; a date reads the numbers as they stood that day.
 */
export const MetricAsOf = Schema.Union([Schema.Literals(["now", "reported"]), IsoDate]);
export type MetricAsOf = typeof MetricAsOf.Type;

export const MetricSortDirection = Schema.Literals(["desc", "asc"]);
export type MetricSortDirection = typeof MetricSortDirection.Type;

/** Most rows a ranked query may keep ("top 10"). */
export const METRIC_MAX_LIMIT = 50;

const MetricQueryFields = Schema.Struct({
  metrics: Schema.Array(MetricId).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(METRIC_LIMITS.metricsPerQuery),
  ),
  /** Splits; each becomes an output column named by the dimension id. */
  dimensions: Schema.Array(Identifier).check(Schema.isMaxLength(METRIC_LIMITS.dimensionsPerQuery)),
  filters: Schema.Array(MetricFilter).check(Schema.isMaxLength(METRIC_LIMITS.filtersPerQuery)),
  dateRange: MetricDateRange,
  grain: MetricQueryGrain,
  /** Adds `compare_value`, computed from summed counts for ratios. */
  compareTo: Schema.optionalKey(MetricComparison),
  asOf: MetricAsOf,
  /**
   * Ranks the splits by one of the query's metrics ("top 5 navigators"). Only for
   * a `total` grain with at least one dimension.
   */
  orderBy: Schema.optionalKey(Schema.Struct({ metric: MetricId, direction: MetricSortDirection })),
  /** Keeps the first N ranked splits; needs `orderBy`. */
  limit: Schema.optionalKey(
    Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: METRIC_MAX_LIMIT })),
  ),
});

/**
 * Output columns: `metric` (the metric id), `period` (the period start, absent
 * for a `total` grain), one column per dimension, `value`, `numerator` and
 * `denominator` (ratios only) and `compare_value` (with `compareTo`).
 */
export const MetricQuery = MetricQueryFields.check(
  Schema.makeFilter(
    (query) => {
      const metric = findDuplicate(query.metrics);
      if (metric) return { path: ["metrics"], message: `Duplicate metric "${metric}"` };
      const dimension = findDuplicate(query.dimensions);
      if (dimension) return { path: ["dimensions"], message: `Duplicate dimension "${dimension}"` };
      const reserved = query.dimensions.findIndex((id) =>
        (METRIC_OUTPUT_COLUMNS as ReadonlyArray<string>).includes(id),
      );
      if (reserved !== -1) {
        return {
          path: ["dimensions", reserved],
          message: `"${query.dimensions[reserved]}" is a metric output column name`,
        };
      }
      if ("start" in query.dateRange && query.dateRange.start > query.dateRange.end) {
        return { path: ["dateRange"], message: "Start must not be after end" };
      }
      if (query.orderBy) {
        if (!query.metrics.includes(query.orderBy.metric)) {
          return { path: ["orderBy", "metric"], message: "Order by one of the query's metrics" };
        }
        if (query.grain !== "total" || query.dimensions.length === 0) {
          return {
            path: ["orderBy"],
            message: "Ranking needs a total grain and at least one dimension",
          };
        }
      }
      if (query.limit !== undefined && !query.orderBy) {
        return { path: ["limit"], message: "A limit needs orderBy" };
      }
      return undefined;
    },
    { identifier: "MetricQuery" },
  ),
);
export type MetricQuery = typeof MetricQuery.Type;

// Values -------------------------------------------------------------------

export const MetricValueStatus = Schema.Literals(["settled", "settling"]);
export type MetricValueStatus = typeof MetricValueStatus.Type;

/** Both dates inclusive. */
export const MetricPeriod = Schema.Struct({ start: IsoDate, end: IsoDate });
export type MetricPeriod = typeof MetricPeriod.Type;

/** Counts under this are suppressed, as small cells can identify members. */
export const METRIC_SUPPRESSION_THRESHOLD = 11;

const MetricValueFields = Schema.Struct({
  metricId: MetricId,
  period: MetricPeriod,
  /** One entry per query dimension, keyed by dimension id. */
  dimensions: Schema.Record(Identifier, DashboardArtifactCell),
  value: Schema.NullOr(Schema.Finite),
  numerator: Schema.optionalKey(Schema.NullOr(Schema.Finite)),
  denominator: Schema.optionalKey(Schema.NullOr(Schema.Finite)),
  compareValue: Schema.optionalKey(Schema.NullOr(Schema.Finite)),
  /** True when an underlying count is under 11; the numbers are then null. */
  suppressed: Schema.optionalKey(Schema.Boolean),
  status: MetricValueStatus,
  /** The source table's last successful run. */
  computedAt: IsoTimestamp,
});

export const MetricValue = MetricValueFields.check(
  Schema.makeFilter(
    (value) =>
      !value.suppressed ||
      (value.value === null && (value.numerator ?? null) === null) ||
      "A suppressed value carries no numbers",
    { identifier: "MetricValue" },
  ),
);
export type MetricValue = typeof MetricValue.Type;

// Provenance ---------------------------------------------------------------

/** Ad-hoc SQL over clean tables, or over the raw replica (always unverified). */
export const AdHocDataSource = Schema.Literals(["clean_tables", "raw_replica"]);
export type AdHocDataSource = typeof AdHocDataSource.Type;

export const MetricProvenance = Schema.Struct({
  kind: Schema.Literal("metric"),
  metricId: MetricId,
  title: Label,
  description: DefinitionText,
  verified: MetricVerification,
  definitionVersion: PositiveCount,
  /** How the metric's values read; clients format `value` and `compare_value` with it. */
  format: Schema.optionalKey(MetricFormat),
});
export type MetricProvenance = typeof MetricProvenance.Type;

export const AdHocProvenance = Schema.Struct({
  kind: Schema.Literal("ad_hoc"),
  definition: DefinitionText,
  source: AdHocDataSource,
  datasetId: Schema.optionalKey(Identifier),
});
export type AdHocProvenance = typeof AdHocProvenance.Type;

/** Where a number came from: a registry metric, or ad-hoc SQL. */
export const Provenance = Schema.Union([MetricProvenance, AdHocProvenance]);
export type Provenance = typeof Provenance.Type;

// Change explanations ------------------------------------------------------

export const MetricDefinitionChange = Schema.Struct({
  definitionVersion: PositiveCount,
  changedAt: IsoTimestamp,
  description: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
  /** How much this change moved the number. */
  change: Schema.Finite,
});
export type MetricDefinitionChange = typeof MetricDefinitionChange.Type;

export const MetricDimensionChange = Schema.Struct({
  dimension: Identifier,
  values: Schema.Array(
    Schema.Struct({ value: DashboardArtifactCell, reported: Schema.Finite, now: Schema.Finite }),
  ),
});
export type MetricDimensionChange = typeof MetricDimensionChange.Type;

/** Relative tolerance for "the parts sum to the change". */
export const CHANGE_EXPLANATION_TOLERANCE = 1e-6;

const ChangeExplanationFields = Schema.Struct({
  /** The number as reported when the period closed. */
  reported: Schema.Finite,
  now: Schema.Finite,
  /** Late or corrected source data. */
  dataChange: Schema.Finite,
  definitionChanges: Schema.Array(MetricDefinitionChange).check(
    Schema.isMaxLength(METRIC_LIMITS.definitionChanges),
  ),
  /** Records that moved between dimension values (for example a new navigator). */
  reassignment: Schema.Finite,
  byDimension: Schema.optionalKey(MetricDimensionChange),
  /** Reported-at-close history is kept from this date; earlier periods cannot be explained. */
  historySince: Schema.optionalKey(IsoDate),
});

/**
 * Why a closed period's number moved: `reported + dataChange + Σ
 * definitionChanges[].change + reassignment = now`.
 */
export const ChangeExplanation = ChangeExplanationFields.check(
  Schema.makeFilter(
    (explanation) => {
      const total =
        explanation.reported +
        explanation.dataChange +
        explanation.reassignment +
        explanation.definitionChanges.reduce((sum, change) => sum + change.change, 0);
      const tolerance = CHANGE_EXPLANATION_TOLERANCE * Math.max(1, Math.abs(explanation.now));
      return (
        Math.abs(total - explanation.now) <= tolerance ||
        "reported + dataChange + definitionChanges + reassignment must equal now"
      );
    },
    { identifier: "ChangeExplanation" },
  ),
);
export type ChangeExplanation = typeof ChangeExplanation.Type;
